import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, makeStyles, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumPais } from '../../../../api'
import { AnnexedParamsModel } from '../../../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import { AnnexedContractModel } from '../../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumCommodity } from '../../../../api/Argentia/enums'
import { EnumTipoValor } from '../../../../api/oData/enums/enumTipoValor'
import { AccordionSection } from '../../../../components'
import { enumToSelectOptionArray } from '../../../../helpers'
import { missingFieldsForm } from '../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { ServiceResponse } from '../../../../model/serviceResponse'
import { getCustomerDomain } from '../../../../redux'
import {
  mapAnnexedContractToPatchRequest,
  mapAnnexedContractToPostRequest,
  mapAnnexedParamsToSelectOption,
  mapAnnexedParamValuesToSelectOption,
  mapSelectedRowToAnnexedContractForm
} from '../../contracts.mapper'
import {
  createAnnexedContract,
  deleteAnnexedContract,
  fetchAnnexedContracts,
  fetchContractByID,
  updateAnnexedContract
} from '../../contracts.service'
import { styles } from '../../contracts.styles'

export interface AnnexedContractsSectionProps {
  data?: AnnexedContractModel[]
  contractId: number
  commodity?: EnumCommodity
  country?: EnumPais
}

export interface AnnexedContractForm {
  annexedParamId?: number
  annexedContractId?: number
  valueTypeId?: EnumTipoValor
  value_text?: string
  value_number?: number
  value_date?: Date
  value_bool?: boolean
  value_list?: number
  value_list_label?: string
}

export const AnnexedContractsSection: React.FC<AnnexedContractsSectionProps> = ({
  data,
  contractId,
  commodity,
  country
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'contracts', 'enums'])
  const {
    openForm,
    updateFormData,
    updateFormDefinition,
    closeForm,
    formProps,
    formHookProps,
    formHookPropsRef,
    CustomFormDialog,
    setFormWaiting
  } = useFormDialog()
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [annexedParamsTypeContract, setAnnexedParamsTypeContract] = useState<AnnexedParamsModel[]>(
    []
  )
  const [annexedContractFormData, setAnnexedContractFormData] = useState<AnnexedContractForm>(
    {} as AnnexedContractForm
  )
  const [tableData, setTableData] = useState<AnnexedContractModel[] | undefined>(data)
  const [formMode, setFormMode] = useState<FormDialogMode | null>(null) //state for modal Form Management
  const [formValueType, setFormValueType] = useState<number>()

  const [annexedParamsTypeContractOptions, setAnnexedParamsTypeContractOptions] = useState<
    SelectOption[]
  >([])
  const [annexedParamsListOptions, setAnnexedParamsListOptions] = useState<SelectOption[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFetchingAnnexedContracts, setIsFetchingAnnexedContracts] = useState<boolean>(false)

  // #region Handlers Starts
  const onAddAnnexedContract = () => {
    setFormMode(FormDialogMode.ADD)
    openForm({
      definition: addAnnexedContractFormDefinition,
      title: t('contracts:components.annexedContracts.annexedParamsForm.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddAnnexedContractCloseHandler
    })
    annexedParamsTypeContract.length > 0 ? annexedParamsTypeContract : getAnnexedContracts()
  }

  useEffect(() => {
    updateTableData()
  }, [contractId])

  const updateTableData = async () => {
    setIsLoading(true)

    await Promise.all([fetchContractByID(contractId), getAnnexedContracts()])
      .then(([contractResponse, annexedResponse]) => {
        if (contractResponse.success && contractResponse.data) {
          // setTableData(contractResponse.data.annexedContracts)
          const paramsDataResponse = annexedResponse.data

          const formattedData = contractResponse.data.annexedContracts.map(row => {
            if (row.valueType === EnumTipoValor.List && paramsDataResponse) {
              const paramInfo = paramsDataResponse.find(param => param.id === row.annexedParam)

              if (paramInfo?.annexedParamsValues) {
                const paramValue = paramInfo.annexedParamsValues.find(pv => pv.code === row.value)
                if (paramValue) {
                  return {
                    ...row,
                    value: `${paramValue.code} - ${paramValue.description ?? ''}`
                  }
                }
              }
            }
            return row
          })
          setTableData(formattedData)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onAddAnnexedContractCloseHandler = async formData => {
    if (formData) {
      if (!missingFieldsForm(addAnnexedContractFormDefinitionRef.current.sections!, formData)) {
        let request = mapAnnexedContractToPostRequest(formData)
        await createAnnexedContract([request], contractId).then(async response => {
          if (response.success) {
            info(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.addForm.success'
              )
            )
            closeForm()
            await updateTableData()
          } else {
            error(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.addForm.error'
              )
            )
          }
        })
      } else {
        error(
          t('contracts:components.annexedContracts.annexedParamsForm.notifications.incompleteForm')
        )
        return
      }
    } else {
      closeForm()
    }
    setFormMode(null)
    setFormValueType(undefined)
  }

  const getAnnexedParamValueId = (
    rowParamId: number,
    paramLabel: string,
    paramsList: AnnexedParamsModel[]
  ) => {
    const paramValues = paramsList.find(param => param.id === rowParamId)?.annexedParamsValues
    const id = paramValues?.find(paramValue => paramValue.description === paramLabel)?.id
    return id
  }

  const onEditAnnexedContract = async (
    selectedRow: AnnexedContractModel,
    annexedParams: AnnexedParamsModel[]
  ) => {
    const selectedParamInfo = annexedParams.find(param => param.id === selectedRow.annexedParam)

    let inputData: AnnexedContractForm = mapSelectedRowToAnnexedContractForm(
      selectedRow,
      selectedParamInfo?.annexedParamsValues
    )
    if (selectedRow.valueType === EnumTipoValor.List) {
      const code = selectedRow.value.split(' - ')[0]

      inputData = {
        ...inputData,
        value_list: selectedParamInfo?.annexedParamsValues?.find(pv => pv.code === code)?.id
      }

      setAnnexedParamsListOptions(
        mapAnnexedParamValuesToSelectOption(selectedParamInfo?.annexedParamsValues ?? [])
      )
    }
    setFormMode(FormDialogMode.EDIT)
    openForm({
      definition: addAnnexedContractFormDefinition,
      title: t('contracts:components.annexedContracts.annexedParamsForm.editTitle'),
      mode: FormDialogMode.EDIT,
      inputData: inputData,
      isWaiting: false,
      onClose: onEditAnnexedContractCloseHandler
    })
    setFormValueType(selectedRow.valueType)
  }

  const onEditAnnexedContractCloseHandler = async formData => {
    if (formData) {
      let request = mapAnnexedContractToPatchRequest(formData, contractId)
      await updateAnnexedContract(contractId, request.annexedContractId, request).then(
        async response => {
          if (response.success) {
            info(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.editForm.success'
              )
            )
            closeForm()
            await updateTableData()
          } else {
            error(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.editForm.error'
              )
            )
            return
          }
        }
      )
    }
    closeForm()
    setFormMode(null)
    setFormValueType(undefined)
  }

  const onDeleteAnnexedContract = async (selectedRow: AnnexedContractModel) => {
    getConfirmation(ConfirmationDialogs.DELETE_ANNEXED_CONTRACT).then(async () => {
      await deleteAnnexedContract(selectedRow.contractId, selectedRow.annexedContractId).then(
        response => {
          if (response.success) {
            info(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.delete.success'
              )
            )
            updateTableData()
          } else {
            error(
              t(
                'contracts:components.annexedContracts.annexedParamsForm.notifications.delete.error'
              )
            )
          }
        }
      )
      closeConfirmation()
    })
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const valueTypeIsEqual = useCallback(
    (inputValue: number | undefined, expectedValue: EnumTipoValor) => {
      return inputValue ? inputValue === expectedValue : false
    },
    []
  )

  const filterUsedParams = useCallback(
    (paramsListArray: SelectOption[]) => {
      const filteredParams = paramsListArray.filter(
        param => !tableData?.some(contract => contract.annexedParam === param.value)
      )
      return filteredParams
    },
    [tableData]
  )
  // #endregion Functions Ends

  // #region Form Definition Starts
  const addAnnexedContractFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    (): FormDialogDefinitionProps => ({
      id: 'ADD_ANNEXED_PARAMS',
      sections: [
        {
          inputs: [
            {
              name: 'annexedParamId',
              label: t(
                'contracts:components.annexedContracts.annexedParamsForm.inputs.annexedParamId'
              ),
              type: DataType.SELECT,
              selectOptions:
                formMode === FormDialogMode.ADD
                  ? filterUsedParams(annexedParamsTypeContractOptions)
                  : annexedParamsTypeContractOptions,
              isLoading: isFetchingAnnexedContracts,
              inputSize: 4,
              readOnly: formMode === FormDialogMode.EDIT ? true : false,
              required: true,
              onChange: (value, formState) => {
                const annexedParam = annexedParamsTypeContract.find(param => param.id === value)
                updateFormData(prevState => ({
                  annexedParamId: +value,
                  valueTypeId: annexedParam?.valueTypeId,
                  value_text:
                    annexedParam?.valueTypeId === EnumTipoValor.Char ||
                    annexedParam?.valueTypeId === EnumTipoValor.String
                      ? ''
                      : undefined,
                  value_number:
                    annexedParam?.valueTypeId === EnumTipoValor.Int ||
                    annexedParam?.valueTypeId === EnumTipoValor.Decimal
                      ? 0
                      : undefined,
                  value_date:
                    annexedParam?.valueTypeId === EnumTipoValor.DateTime
                      ? moment().toDate()
                      : undefined,
                  value_bool: annexedParam?.valueTypeId === EnumTipoValor.Bool ? false : undefined,
                  value_list:
                    annexedParam?.valueTypeId === EnumTipoValor.List
                      ? annexedParam?.annexedParamsValues![0].id
                      : undefined,
                  value_list_label:
                    annexedParam?.valueTypeId === EnumTipoValor.List
                      ? `${annexedParam?.annexedParamsValues![0].code} - ${annexedParam?.annexedParamsValues![0].description}`
                      : undefined
                }))
                setFormValueType(annexedParam?.valueTypeId)

                if (annexedParam?.valueTypeId === EnumTipoValor.List && annexedParam) {
                  setAnnexedParamsListOptions(
                    mapAnnexedParamValuesToSelectOption(annexedParam?.annexedParamsValues ?? [])
                  )
                }
              }
            },
            {
              name: 'valueTypeId',
              label: t(
                'contracts:components.annexedContracts.annexedParamsForm.inputs.valueTypeId'
              ),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumTipoValor, 'enums:EnumTipoValor'),
              readOnly: true,
              inputSize: 4
            },
            {
              name: 'value_text',
              label: t('contracts:components.annexedContracts.annexedParamsForm.inputs.value'),
              type: DataType.TEXT,
              defaultValue: null,
              maxLength: formValueType === EnumTipoValor.Char ? 1 : 100,
              inputSize: 4,
              helpText:
                formValueType === EnumTipoValor.Char
                  ? t('contracts:components.annexedContracts.annexedParamsForm.helpText.char')
                  : t('contracts:components.annexedContracts.annexedParamsForm.helpText.string'),
              display:
                valueTypeIsEqual(formValueType, EnumTipoValor.Char) ||
                valueTypeIsEqual(formValueType, EnumTipoValor.String),
              required:
                valueTypeIsEqual(formValueType, EnumTipoValor.Char) ||
                valueTypeIsEqual(formValueType, EnumTipoValor.String)
            },
            {
              name: 'value_number',
              label: t('contracts:components.annexedContracts.annexedParamsForm.inputs.value'),
              type: DataType.NUMBER,
              customDecimalScale: formValueType === EnumTipoValor.Int ? 0 : 6,
              inputSize: 4,
              helpText:
                formValueType === EnumTipoValor.Int
                  ? t('contracts:components.annexedContracts.annexedParamsForm.helpText.int')
                  : undefined,
              display:
                valueTypeIsEqual(formValueType, EnumTipoValor.Int) ||
                valueTypeIsEqual(formValueType, EnumTipoValor.Decimal),
              required:
                valueTypeIsEqual(formValueType, EnumTipoValor.Int) ||
                valueTypeIsEqual(formValueType, EnumTipoValor.Decimal)
            },
            {
              name: 'value_date',
              label: t('contracts:components.annexedContracts.annexedParamsForm.inputs.value'),
              type: DataType.DATE,
              inputSize: 4,
              display: valueTypeIsEqual(formValueType, EnumTipoValor.DateTime),
              required: valueTypeIsEqual(formValueType, EnumTipoValor.DateTime)
            },
            {
              name: 'value_bool',
              label: t(
                'contracts:components.annexedContracts.annexedParamsForm.inputs.valueSwitch'
              ),
              type: DataType.SWITCH,
              inputSize: 4,
              display: valueTypeIsEqual(formValueType, EnumTipoValor.Bool),
              required: valueTypeIsEqual(formValueType, EnumTipoValor.Bool)
            },
            {
              name: 'value_list',
              label: t('contracts:components.annexedContracts.annexedParamsForm.inputs.value'),
              type: DataType.SELECT,
              inputSize: 4,
              selectOptions: annexedParamsListOptions,
              display: valueTypeIsEqual(formValueType, EnumTipoValor.List),
              required: valueTypeIsEqual(formValueType, EnumTipoValor.List),
              onChange: (value, formState) => {
                updateFormData(prevState => ({
                  ...prevState,
                  value_list_label: annexedParamsListOptions.find(param => param.value === +value)
                    ?.label
                }))
              }
            },
            {
              name: 'value_list_label',
              display: false
            }
          ]
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      isFetchingAnnexedContracts,
      annexedParamsTypeContractOptions,
      valueTypeIsEqual,
      filterUsedParams,
      formValueType,
      annexedParamsListOptions,
      annexedContractFormData,
      formMode,
      annexedParamsTypeContract
    ]
  )
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const annexedParamsTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    download: false,
    canInsert: true,
    customInsertEditModal: true,
    // onRowInsert: async () => {
    //   annexedParamsTypeContract.length > 0
    //     ? onAddAnnexedContract()
    //     : await getAnnexedContracts().then(response => {
    //         if (response.success) {
    //           onAddAnnexedContract()
    //         }
    //       })

    //   return true
    // },
    onRowInsert: async () => {
      onAddAnnexedContract()
      return true
    },
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        tooltip: t(
          'contracts:components.annexedContracts.annexedParamsTable.dataTable.actions.edit'
        ),
        onClick: async (selectedRow: AnnexedContractModel) => {
          annexedParamsTypeContract.length > 0
            ? onEditAnnexedContract(selectedRow, annexedParamsTypeContract)
            : await getAnnexedContracts().then(response => {
                if (response.success && response.data) {
                  onEditAnnexedContract(selectedRow, response.data)
                }
              })
        }
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t(
          'contracts:components.annexedContracts.annexedParamsTable.dataTable.actions.delete'
        ),
        onClick: onDeleteAnnexedContract
      }
    ],
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:components.annexedContracts.annexedParamsTable.dataTable.analytics')
    }
  }

  const annexedParamsColumns: DataTableColumn[] = [
    {
      name: 'contractId',
      display: false
    },
    {
      name: 'annexedContractId',
      display: false
    },
    {
      name: 'description',
      label: t(
        'contracts:components.annexedContracts.annexedParamsTable.dataTable.columns.description'
      ),
      type: DataType.TEXT
    },
    {
      name: 'detailedDescription',
      label: t(
        'contracts:components.annexedContracts.annexedParamsTable.dataTable.columns.detailedDescription'
      ),
      type: DataType.TEXT
    },
    {
      name: 'valueType',
      label: t(
        'contracts:components.annexedContracts.annexedParamsTable.dataTable.columns.valueType'
      ),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoValor, 'enums:EnumTipoValor')
    },
    {
      name: 'value',
      label: t('contracts:components.annexedContracts.annexedParamsTable.dataTable.columns.value'),
      type: DataType.TEXT
    }
  ]
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const getAnnexedContracts = async (): Promise<
    ServiceResponse<AnnexedParamsModel[] | undefined>
  > => {
    setIsFetchingAnnexedContracts(true)
    return fetchAnnexedContracts(commodity, country)
      .then(response => {
        if (response.data !== undefined) {
          setAnnexedParamsTypeContract(response.data)
          setAnnexedParamsTypeContractOptions(mapAnnexedParamsToSelectOption(response.data))

          // setAnnexedParamsTypeContractOptions(filterUsedParams(response.data))
        }
        setIsFetchingAnnexedContracts(false)

        return response
      })
      .catch(err => {
        setIsFetchingAnnexedContracts(false)
        return err
      })
  }

  const getFormattedTableData = useCallback((): AnnexedContractModel[] | undefined => {
    if (!tableData) return undefined
    return tableData.map(row => {
      if (row.valueType === EnumTipoValor.List) {
        const paramInfo = annexedParamsTypeContract.find(param => param.id === row.annexedParam)
        const paramValue = paramInfo?.annexedParamsValues?.find(pv => pv.code === row.value)
        if (paramValue) {
          return {
            ...row,
            value: `${paramValue.code} - ${paramValue.description ?? ''}`
          }
        }
      }
      return row
    })
  }, [tableData, annexedParamsTypeContract])

  const addAnnexedContractFormDefinitionRef = useRef(addAnnexedContractFormDefinition)
  useEffect(() => {
    addAnnexedContractFormDefinitionRef.current = addAnnexedContractFormDefinition
  }, [addAnnexedContractFormDefinition])

  useEffect(() => {
    updateFormDefinition(addAnnexedContractFormDefinition)
  }, [addAnnexedContractFormDefinition])

  // #endregion Hooks Ends

  return (
    <AccordionSection
      sectionTitle={t('contracts:components.annexedContracts.annexedParamsTable.sectionTitle')}
      style={{
        margin: '0px 16px 16px 16px',
        paddingBottom: '16px'
      }}
    >
      <Box style={{ width: '100%', overflow: 'auto' }}>
        {isLoading ? (
          <DataTableSkeleton rows={3} elevation={0} />
        ) : (
          <DataTable
            className={classes.annexedContractsDataTable}
            data={tableData}
            columns={annexedParamsColumns}
            options={annexedParamsTableOptions}
          />
        )}
      </Box>
      <CustomFormDialog {...formProps} />
    </AccordionSection>
  )
}
